/**
 * PR-AdminUsers-6 — /admin/users/:userId
 * Detalle de usuario con 5 tabs: General, Segmentos, Áreas, Roles globales, Audit.
 */
import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, KeyRound, Lock, Unlock, ShieldOff } from "lucide-react";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  useAdminUser,
  useAdminSegments,
  useAdminAreas,
  useAssignSegments,
  useAssignAreaRoles,
  useAssignGlobalRoles,
  usePasswordReset,
  useLockUser,
  useUnlockUser,
  useDisable2FA,
  useUserAuditLog,
} from "@/hooks/useAdminUsers";
import { cn } from "@/lib/utils";

type Tab = "general" | "segmentos" | "areas" | "roles" | "audit";

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("general");
  const [tempPass, setTempPass] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const user = useAdminUser(userId ?? null);
  const segments = useAdminSegments({ active_only: true });
  const areas = useAdminAreas();
  const auditLog = useUserAuditLog(tab === "audit" ? (userId ?? null) : null);

  const assignSegments = useAssignSegments(userId!);
  const assignAreaRoles = useAssignAreaRoles(userId!);
  const assignGlobalRoles = useAssignGlobalRoles(userId!);
  const passwordReset = usePasswordReset(userId!);
  const lockUser = useLockUser(userId!);
  const unlockUser = useUnlockUser(userId!);
  const disable2FA = useDisable2FA(userId!);

  if (!userId) return null;
  if (user.isPending) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (user.isError) return <p className="text-sm text-destructive">Usuario no encontrado.</p>;
  const u = user.data;

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "general", label: "General" },
    { id: "segmentos", label: "Segmentos" },
    { id: "areas", label: "Áreas" },
    { id: "roles", label: "Roles globales" },
    { id: "audit", label: "Audit" },
  ];

  const handleAction = async (fn: () => Promise<unknown>) => {
    setActionError(null);
    try {
      await fn();
      void user.refetch();
    } catch (e) {
      if (e instanceof ApiError) setActionError(e.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/admin/users")} className="text-muted-foreground hover:text-foreground">
          <ArrowLeft className="size-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold">{u.username}</h1>
          <p className="text-sm text-muted-foreground">
            {u.role} · {u.is_active ? "Activo" : "Inactivo"} · 2FA: {u.has_2fa ? "Activo" : "No"}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              "px-4 py-2 text-sm font-medium border-b-2 transition-colors",
              tab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {actionError && (
        <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          {actionError}
        </p>
      )}

      {/* ── General ──────────────────────────────────────────────────────── */}
      {tab === "general" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Row label="ID" value={u.id} mono />
            <Row label="Username" value={u.username} />
            <Row label="Rol" value={u.role} />
            <Row label="Activo" value={u.is_active ? "Sí" : "No"} />
            <Row label="2FA" value={u.has_2fa ? "Activo" : "No enrolado"} />
            <Row label="Telegram" value={u.telegram_chat_id ?? "—"} />
            <Row label="Creado" value={new Date(u.created_at).toLocaleString("es-MX")} />
            <Row
              label="Último login"
              value={u.last_login_at ? new Date(u.last_login_at).toLocaleString("es-MX") : "—"}
            />
          </div>

          <div className="border-t pt-4 space-y-2">
            <p className="text-sm font-medium text-muted-foreground mb-3">Acciones de seguridad</p>

            {/* Password reset */}
            {tempPass ? (
              <div className="rounded-md border bg-muted/30 p-3 space-y-2">
                <p className="text-xs font-medium">Password temporal (solo visible una vez):</p>
                <code className="block rounded bg-muted px-3 py-2 font-mono text-sm select-all">
                  {tempPass}
                </code>
                <Button size="sm" variant="ghost" onClick={() => setTempPass(null)}>
                  Ocultar
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleAction(async () => {
                  const res = await passwordReset.mutateAsync();
                  setTempPass(res.temp_password);
                })}
                disabled={passwordReset.isPending}
              >
                <KeyRound className="size-4" /> Resetear password
              </Button>
            )}

            {/* Lock / Unlock */}
            {u.is_active ? (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => void handleAction(() => lockUser.mutateAsync())}
                disabled={lockUser.isPending}
              >
                <Lock className="size-4" /> Bloquear cuenta
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleAction(() => unlockUser.mutateAsync())}
                disabled={unlockUser.isPending}
              >
                <Unlock className="size-4" /> Desbloquear cuenta
              </Button>
            )}

            {/* 2FA disable */}
            {u.has_2fa && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => void handleAction(() => disable2FA.mutateAsync())}
                disabled={disable2FA.isPending}
              >
                <ShieldOff className="size-4" /> Desactivar 2FA
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Segmentos ─────────────────────────────────────────────────────── */}
      {tab === "segmentos" && (
        <SegmentosTab
          userSegments={u.segments}
          allSegments={segments.data?.items ?? []}
          onSave={async (ids) => {
            await assignSegments.mutateAsync(ids);
            void user.refetch();
          }}
          saving={assignSegments.isPending}
        />
      )}

      {/* ── Áreas ─────────────────────────────────────────────────────────── */}
      {tab === "areas" && (
        <AreasTab
          userAreaRoles={u.area_roles}
          allAreas={areas.data?.items ?? []}
          onSave={async (items) => {
            await assignAreaRoles.mutateAsync(items);
            void user.refetch();
          }}
          saving={assignAreaRoles.isPending}
        />
      )}

      {/* ── Roles globales ─────────────────────────────────────────────────── */}
      {tab === "roles" && (
        <GlobalRolesTab
          userRoles={u.global_roles}
          onSave={async (roles) => {
            await assignGlobalRoles.mutateAsync(roles);
            void user.refetch();
          }}
          saving={assignGlobalRoles.isPending}
        />
      )}

      {/* ── Audit ────────────────────────────────────────────────────────── */}
      {tab === "audit" && (
        <AuditTab items={auditLog.data?.items ?? []} loading={auditLog.isPending} />
      )}
    </div>
  );
}

// ── Sub-componentes ────────────────────────────────────────────────────────

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <span className="text-muted-foreground">{label}: </span>
      <span className={mono ? "font-mono text-xs" : ""}>{value}</span>
    </div>
  );
}

function SegmentosTab({
  userSegments,
  allSegments,
  onSave,
  saving,
}: {
  userSegments: string[];
  allSegments: Array<{ id: string; code: string; name: string }>;
  onSave: (ids: string[]) => Promise<void>;
  saving: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(
    new Set(allSegments.filter(s => userSegments.includes(s.code)).map(s => s.id)),
  );
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setError(null);
    try {
      await onSave([...selected]);
      setSaved(true);
    } catch {
      setError("Error al guardar segmentos.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-2">
        {allSegments.map(s => (
          <label key={s.id} className="flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer hover:bg-accent/50">
            <input
              type="checkbox"
              checked={selected.has(s.id)}
              onChange={() => toggle(s.id)}
              className="size-4"
            />
            <div>
              <div className="text-sm font-medium">{s.name}</div>
              <div className="text-xs text-muted-foreground font-mono">{s.code}</div>
            </div>
          </label>
        ))}
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-3">
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Guardando…" : "Guardar segmentos"}
        </Button>
        {saved && <span className="text-xs text-emerald-600">Guardado.</span>}
      </div>
    </div>
  );
}

function AreasTab({
  userAreaRoles,
  allAreas,
  onSave,
  saving,
}: {
  userAreaRoles: Array<{ area_id: string; area_code: string; area_name: string; role: string }>;
  allAreas: Array<{ id: string; code: string; name: string }>;
  onSave: (items: Array<{ area_id: string; role: string }>) => Promise<void>;
  saving: boolean;
}) {
  const initial = new Map(userAreaRoles.map(ar => [ar.area_id, ar.role]));
  const [roleMap, setRoleMap] = useState<Map<string, string | null>>(initial);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setRole = (areaId: string, role: string | null) => {
    setRoleMap(prev => new Map(prev).set(areaId, role));
    setSaved(false);
  };

  const handleSave = async () => {
    setError(null);
    const items: Array<{ area_id: string; role: string }> = [];
    roleMap.forEach((role, area_id) => {
      if (role) items.push({ area_id, role });
    });
    try {
      await onSave(items);
      setSaved(true);
    } catch {
      setError("Error al guardar áreas.");
    }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-md border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Área</th>
              <th className="px-4 py-2 text-left font-medium">Código</th>
              <th className="px-4 py-2 text-left font-medium">Rol</th>
            </tr>
          </thead>
          <tbody>
            {allAreas.map(a => (
              <tr key={a.id} className="border-t">
                <td className="px-4 py-2">{a.name}</td>
                <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{a.code}</td>
                <td className="px-4 py-2">
                  <select
                    value={roleMap.get(a.id) ?? ""}
                    onChange={e => setRole(a.id, e.target.value || null)}
                    className="rounded-md border bg-background px-2 py-1 text-sm"
                  >
                    <option value="">Sin acceso</option>
                    <option value="operator">Operador</option>
                    <option value="manager">Manager</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-3">
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Guardando…" : "Guardar áreas"}
        </Button>
        {saved && <span className="text-xs text-emerald-600">Guardado.</span>}
      </div>
    </div>
  );
}

function GlobalRolesTab({
  userRoles,
  onSave,
  saving,
}: {
  userRoles: string[];
  onSave: (roles: string[]) => Promise<void>;
  saving: boolean;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(userRoles));
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (role: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(role) ? next.delete(role) : next.add(role);
      return next;
    });
    setSaved(false);
  };

  const handleSave = async () => {
    setError(null);
    try {
      await onSave([...selected]);
      setSaved(true);
    } catch (e) {
      if (e instanceof ApiError) setError(e.message);
      else setError("Error al guardar roles.");
    }
  };

  return (
    <div className="space-y-4">
      {[
        { id: "super_user", label: "Super usuario", desc: "Acceso completo a todo el sistema." },
        { id: "auditor", label: "Auditor", desc: "Lectura completa incluyendo audit log, sin escritura." },
      ].map(r => (
        <label key={r.id} className="flex items-start gap-3 rounded-md border px-4 py-3 cursor-pointer hover:bg-accent/50">
          <input
            type="checkbox"
            checked={selected.has(r.id)}
            onChange={() => toggle(r.id)}
            className="mt-0.5 size-4"
          />
          <div>
            <div className="text-sm font-medium">{r.label}</div>
            <div className="text-xs text-muted-foreground">{r.desc}</div>
          </div>
        </label>
      ))}
      {error && <p className="text-xs text-destructive">{error}</p>}
      <div className="flex items-center gap-3">
        <Button onClick={() => void handleSave()} disabled={saving}>
          {saving ? "Guardando…" : "Guardar roles globales"}
        </Button>
        {saved && <span className="text-xs text-emerald-600">Guardado.</span>}
      </div>
    </div>
  );
}

function AuditTab({
  items,
  loading,
}: {
  items: Array<{ id: string; action: string; target_type: string; before_value: unknown; after_value: unknown; timestamp: string }>;
  loading: boolean;
}) {
  if (loading) return <p className="text-sm text-muted-foreground">Cargando…</p>;
  if (items.length === 0) return <p className="text-sm text-muted-foreground">Sin entradas de audit.</p>;
  return (
    <div className="space-y-2">
      {items.map(entry => (
        <div key={entry.id} className="rounded-md border px-4 py-3 text-sm space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-mono font-medium">{entry.action}</span>
            <span className="text-xs text-muted-foreground">
              {new Date(entry.timestamp).toLocaleString("es-MX")}
            </span>
          </div>
          <div className="text-xs text-muted-foreground font-mono">
            {entry.target_type}
          </div>
          {entry.after_value != null && (
            <div className="text-xs text-muted-foreground font-mono truncate">
              → {JSON.stringify(entry.after_value)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
